import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessagesSquare } from "lucide-react";

import {
  useAppHeader,
  type AppHeaderConfig,
  type HeaderCrumb
} from "@/components/app-header";
import { AgentMark, BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { usePlayground } from "../hooks/usePlayground";
import { isCaseConversation } from "../utils/caseConversation";
import { formatRelativeTime, parseTimestamp } from "../utils/time";
import { ConversationHeader } from "./ConversationHeader";
import { ConversationsSidebar } from "./ConversationsSidebar";
import { DeleteConversationModal } from "./DeleteConversationModal";
import { MessageList } from "./MessageList";
import { RenameConversationModal } from "./RenameConversationModal";
import {
  SaveToDraftDestinationModal,
  type SaveDestination
} from "./SaveToDraftDestinationModal";
import type { PlaygroundConversation, PlaygroundMessage } from "../types";
import {
  composeDocx,
  createDraft,
  saveDraftProse,
  updateDraft
} from "../../../api/drafts-v2";
import { DraftType, GenerationStatus } from "../../../api/types/draft";
import { triggerDownload } from "../../../helpers/file_helpers";
import { showSubframeToast } from "../../../components/common/CasePrepToast";

/**
 * The case an archived conversation was about, if any. Older conversations
 * carry it on the conversation; otherwise the last turn's case-context
 * selection (stored on the user message) says which case was attached.
 */
function individualIdForConversation(
  conversation: PlaygroundConversation | null,
  messages: PlaygroundMessage[]
): string | null {
  if (conversation?.individual_id) return conversation.individual_id;
  const userTurns = messages.filter((m) => m.role === "user").reverse();
  for (const m of userTurns) {
    const cc = (m.metadata as { caseContext?: unknown } | null | undefined)
      ?.caseContext;
    if (cc && typeof cc === "object") {
      const { enabled, individualId } = cc as {
        enabled?: unknown;
        individualId?: unknown;
      };
      if (enabled && typeof individualId === "string" && individualId) {
        return individualId;
      }
    }
  }
  return null;
}

/** The read-only notice, shown once as the page description. */
const ATHENA_READ_ONLY =
  "Athena is read-only. Past conversations stay here; start new research from a case's chat.";

function ErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="w-full max-w-225 rounded-md border border-solid border-error-200 bg-error-50 px-3 py-2">
      <span className="text-caption font-caption text-error-700">{error}</span>
    </div>
  );
}

export function PlaygroundExperience() {
  const {
    conversations,
    selectedConversationId,
    selectedConversation,
    messages,
    fileUploadsById,
    isLoadingConversations,
    hasLoadedConversations,
    isLoadingConversationData,
    error,
    refreshConversations,
    selectConversation,
    closeConversation,
    renameConversation,
    deleteConversation
  } = usePlayground();

  const [isRenameOpen, setIsRenameOpen] = React.useState(false);
  const [renameDraft, setRenameDraft] = React.useState("");
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [renameConversationId, setRenameConversationId] = React.useState<
    string | null
  >(null);
  const [deleteConversationId, setDeleteConversationId] = React.useState<
    string | null
  >(null);
  const [pendingSaveContent, setPendingSaveContent] = React.useState<
    string | null
  >(null);
  const [isSavingToDraft, setIsSavingToDraft] = React.useState(false);

  const openRename = React.useCallback(
    (playgroundConversationId?: string) => {
      const convoId = playgroundConversationId ?? selectedConversationId;
      if (!convoId) return;
      const c = conversations.find(
        (x) => x.playground_conversation_id === convoId
      );
      setRenameDraft((c?.title ?? "").trim());
      setRenameConversationId(convoId);
      setIsRenameOpen(true);
    },
    [conversations, selectedConversationId]
  );

  const doRename = React.useCallback(async () => {
    if (!renameConversationId) return;
    setIsRenaming(true);
    try {
      const nextTitle = renameDraft.trim();
      await renameConversation({
        playgroundConversationId: renameConversationId,
        title: nextTitle || null
      });
      setIsRenameOpen(false);
    } finally {
      setIsRenaming(false);
    }
  }, [renameConversation, renameConversationId, renameDraft]);

  const selectedConversationTitleForModal = useMemo(() => {
    const id = deleteConversationId ?? selectedConversationId;
    if (!id) return "";
    const c = conversations.find((x) => x.playground_conversation_id === id);
    return (c?.title ?? "").trim() || "Untitled conversation";
  }, [conversations, deleteConversationId, selectedConversationId]);

  const doDelete = React.useCallback(async () => {
    if (!deleteConversationId) return;
    setIsDeleting(true);
    try {
      await deleteConversation({
        playgroundConversationId: deleteConversationId
      });
      setIsDeleteOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConversation, deleteConversationId]);

  // Saving an archived reply into a draft needs the case it belongs to.
  const individualIdForSave = useMemo(
    () => individualIdForConversation(selectedConversation, messages),
    [selectedConversation, messages]
  );

  // Stable identities so the memoized MessageList doesn't re-render needlessly.
  const handleSaveAssistantToDraft = React.useMemo(
    () =>
      individualIdForSave
        ? (content: string) => setPendingSaveContent(content)
        : undefined,
    [individualIdForSave]
  );

  // Download a reply directly as Word/PDF without saving it to a draft.
  const handleDownloadAssistantContent = React.useMemo(
    () => async (content: string, format: "docx" | "pdf") => {
      try {
        const { bytes, filename } = await composeDocx(content, format);
        triggerDownload(bytes, filename, format);
      } catch (e) {
        showSubframeToast({
          variant: "error",
          title: "Couldn't download",
          description: e instanceof Error ? e.message : "Please try again."
        });
      }
    },
    []
  );

  const doSaveToDraft = React.useCallback(
    async (destination: SaveDestination) => {
      if (pendingSaveContent == null) return;
      setIsSavingToDraft(true);
      try {
        let targetDraftId: string;
        let targetLabel: string;

        if (destination.kind === "existing") {
          targetDraftId = destination.draftId;
          targetLabel = destination.label;
        } else {
          if (!individualIdForSave) {
            throw new Error("No case is associated with this conversation.");
          }
          const created = await createDraft({
            individual_id: individualIdForSave,
            draft_type: DraftType.CUSTOM,
            title: destination.title
          });
          targetDraftId = created.draft_id;
          targetLabel = destination.title;
        }

        // The prose is authored into the draft's docx body, then the draft is
        // marked Generated: a save-back always writes fresh, user-approved
        // content, so it becomes previewable and any lingering "Needs Update"
        // flag clears.
        await saveDraftProse(targetDraftId, pendingSaveContent, targetLabel);
        await updateDraft(targetDraftId, {
          status: GenerationStatus.GENERATED
        });
        setPendingSaveContent(null);
        showSubframeToast({
          variant: "success",
          title: "Saved to draft",
          description: `Updated "${targetLabel}".`
        });
      } catch (e) {
        showSubframeToast({
          variant: "error",
          title: "Couldn't save to draft",
          description: e instanceof Error ? e.message : "Please try again."
        });
      } finally {
        setIsSavingToDraft(false);
      }
    },
    [pendingSaveContent, individualIdForSave]
  );

  useEffect(() => {
    refreshConversations().catch(() => {});
  }, [refreshConversations]);

  useEffect(() => {
    if (selectedConversationId) {
      selectConversation(selectedConversationId).catch(() => {});
    }
  }, [selectedConversationId, selectConversation]);

  const selectedConversationTitle = useMemo(() => {
    if (!selectedConversationId) return "Past Conversations";
    const title = (selectedConversation?.title ?? "").trim();
    return title || "Untitled Conversation";
  }, [selectedConversation, selectedConversationId]);

  const breadcrumbSegments = useMemo(
    (): HeaderCrumb[] => [
      {
        id: "athena",
        label: "Athena",
        to: "/athena",
        icon: AgentMark
      },
      {
        id: selectedConversationId ?? "archive",
        label: selectedConversationTitle,
        current: true
      }
    ],
    [selectedConversationId, selectedConversationTitle]
  );

  useAppHeader(
    useMemo(
      (): AppHeaderConfig | null => ({ crumbs: breadcrumbSegments }),
      [breadcrumbSegments]
    )
  );

  const headerSubtitle = useMemo(() => {
    if (!selectedConversationId) return "Read-only archive";
    const date = parseTimestamp(selectedConversation?.last_message_at);
    if (date) return formatRelativeTime(date);
    if (isLoadingConversationData) return "Loading…";
    return "Read-only";
  }, [isLoadingConversationData, selectedConversation, selectedConversationId]);

  const canEditSelected =
    Boolean(selectedConversationId) &&
    !isCaseConversation(selectedConversation);

  const showSidebarLoading = isLoadingConversations && !hasLoadedConversations;
  const showEmptyConversations =
    hasLoadedConversations &&
    !isLoadingConversations &&
    conversations.length === 0;
  const shouldShowMessageList = Boolean(
    selectedConversationId && (messages.length > 0 || isLoadingConversationData)
  );

  const emptyState = (
    <EmptyState
      icon={MessagesSquare}
      title={
        showEmptyConversations
          ? "No Past Athena Conversations"
          : "Select a Conversation"
      }
      description="Athena no longer takes new questions. Research now happens in each case's chat, where the agent can search decisions, policy and the web with the case in hand."
      action={
        <Button variant="secondary" size="sm" asChild>
          <Link to="/cases">
            Go to Cases
            <ArrowRight />
          </Link>
        </Button>
      }
      className="flex-1"
    />
  );

  const modals = (
    <>
      <RenameConversationModal
        isOpen={isRenameOpen}
        title="Rename conversation"
        value={renameDraft}
        onChange={setRenameDraft}
        isSaving={isRenaming}
        onCancel={() => {
          if (isRenaming) return;
          setIsRenameOpen(false);
          setRenameConversationId(null);
        }}
        onSave={() => {
          doRename().catch(() => {});
        }}
      />

      <DeleteConversationModal
        isOpen={isDeleteOpen}
        conversationTitle={selectedConversationTitleForModal}
        isDeleting={isDeleting}
        onCancel={() => {
          if (isDeleting) return;
          setIsDeleteOpen(false);
          setDeleteConversationId(null);
        }}
        onDelete={() => {
          doDelete().catch(() => {});
        }}
      />

      <SaveToDraftDestinationModal
        isOpen={pendingSaveContent !== null}
        individualId={individualIdForSave}
        isSaving={isSavingToDraft}
        onCancel={() => {
          if (isSavingToDraft) return;
          setPendingSaveContent(null);
        }}
        onConfirm={(destination) => {
          doSaveToDraft(destination).catch(() => {});
        }}
      />
    </>
  );

  if (showEmptyConversations) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col bg-surface">
        <PageHeader
          title="Past Conversations"
          description={ATHENA_READ_ONLY}
          className="shrink-0 px-6 pt-6"
        />
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-6 pb-20 mobile:px-4">
          <div className="flex w-full max-w-192 flex-col items-center justify-center gap-4">
            {emptyState}
            <ErrorBanner error={error} />
          </div>
        </div>
        {modals}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-surface">
      <div className="flex min-h-0 w-full flex-1 items-start">
        {showSidebarLoading ? (
          <div className="flex w-72 flex-none flex-col items-start self-stretch overflow-auto border-r border-solid border-neutral-border bg-fill-1 px-4 py-4 mobile:hidden">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Conversations
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Past Athena history
                </span>
              </div>
            </div>
            <div className="flex w-full flex-1 flex-col overflow-auto">
              <div className="flex w-full flex-col items-start gap-2 pb-2 pt-2">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <div
                    key={`sk-${idx}`}
                    className="flex w-full animate-pulse items-center gap-3 rounded-lg bg-fill-1 px-3 py-3"
                  >
                    <div className="h-4 w-4 rounded bg-fill-2" />
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                      <div className="h-3 w-40 rounded bg-fill-2" />
                      <div className="h-3 w-20 rounded bg-fill-2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ConversationsSidebar
            conversations={conversations}
            activeConversationId={selectedConversationId ?? undefined}
            onRenameConversation={(playgroundConversationId) => {
              openRename(playgroundConversationId);
            }}
            onDeleteConversation={(playgroundConversationId) => {
              setDeleteConversationId(playgroundConversationId);
              setIsDeleteOpen(true);
            }}
          />
        )}

        <div className="flex w-full min-w-0 grow shrink-0 basis-0 flex-col items-start self-stretch">
          <ConversationHeader
            title={selectedConversationTitle}
            subtitle={headerSubtitle}
            onCloseConversation={closeConversation}
            onRenameConversation={openRename}
            onDeleteConversation={() => {
              if (!selectedConversationId) return;
              setDeleteConversationId(selectedConversationId);
              setIsDeleteOpen(true);
            }}
            canClose={Boolean(selectedConversationId)}
            canRename={canEditSelected}
            canDelete={canEditSelected}
            showMenu={Boolean(selectedConversationId)}
          />

          <div className="flex min-h-0 w-full flex-1 flex-col bg-default-background">
            <div className="relative flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto px-4 py-6 mobile:px-3 mobile:py-4">
              <div className="flex w-full max-w-225 min-h-0 flex-1 flex-col items-start gap-8">
                {shouldShowMessageList ? (
                  <div className="relative min-h-0 w-full flex-1">
                    <MessageList
                      messages={messages}
                      fileUploadsById={fileUploadsById}
                      onSaveAssistantToDraft={handleSaveAssistantToDraft}
                      onDownloadAssistant={handleDownloadAssistantContent}
                    />

                    {isLoadingConversationData ? (
                      <div className="absolute inset-0 z-20 flex items-center justify-center">
                        <BrandLoader label="Loading conversation" />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  emptyState
                )}
              </div>
            </div>

            {error ? (
              <div className="flex w-full flex-none flex-col items-center px-4 pb-4 mobile:px-3">
                <ErrorBanner error={error} />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {modals}
    </div>
  );
}
